import React from 'react';
import {
  CalendarDaysIcon,
  ClockIcon,
  UserGroupIcon,
  CurrencyPoundIcon,
  DocumentCheckIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  SunIcon,
  CogIcon,
  ShieldCheckIcon,
  EnvelopeIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

const BookShiftsHelp = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center space-x-4">
            <CalendarDaysIcon className="h-12 w-12" />
            <div>
              <h1 className="text-3xl font-bold">Shift Booking Management</h1>
              <p className="text-emerald-100 mt-2">Plan and schedule staff shifts with automated cost tracking and policy compliance</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border-l-4 border-emerald-500">
          <div className="flex items-start space-x-4">
            <CalendarDaysIcon className="h-8 w-8 text-emerald-600 mt-1" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">System Overview</h2>
              <p className="text-gray-600 leading-relaxed">
                The Shift Booking system allows managers to plan weekly staff schedules, track working hours against contracts,
                calculate costs automatically, and ensure compliance with break policies. Staff receive email notifications when shifts are published.
              </p>
            </div>
          </div>
        </div>

        {/* Quick Start Workflow */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <CheckCircleIcon className="h-6 w-6 text-emerald-600 mr-2" />
            4-Step Shift Planning Process
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-emerald-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-emerald-600">1</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Navigate to Week</h3>
              <p className="text-sm text-gray-600">Use arrow buttons to select the week you want to schedule</p>
            </div>

            <div className="text-center">
              <div className="bg-teal-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-teal-600">2</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Enter Shift Times</h3>
              <p className="text-sm text-gray-600">Input start and end times for each staff member each day</p>
            </div>

            <div className="text-center">
              <div className="bg-cyan-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-cyan-600">3</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Review Totals</h3>
              <p className="text-sm text-gray-600">Check hours, days, and costs against contracted amounts</p>
            </div>

            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">4</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Publish Shifts</h3>
              <p className="text-sm text-gray-600">Save and notify staff via automatic email notifications</p>
            </div>
          </div>
        </div>

        {/* Interface Guide */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <CogIcon className="h-6 w-6 text-emerald-600 mr-2" />
            Interface Navigation
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Control Buttons</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <ArrowPathIcon className="h-5 w-5 text-purple-600" />
                  <div>
                    <span className="font-medium text-gray-900">Task Manager</span>
                    <p className="text-sm text-gray-600">Switch to task calendar view</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <DocumentCheckIcon className="h-5 w-5 text-emerald-600" />
                  <div>
                    <span className="font-medium text-gray-900">Copy For Next Week</span>
                    <p className="text-sm text-gray-600">Duplicate current week's shifts to next week</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <CalendarDaysIcon className="h-5 w-5 text-blue-600" />
                  <div>
                    <span className="font-medium text-gray-900">Show/Hide Booked Shifts</span>
                    <p className="text-sm text-gray-600">Toggle visibility of existing shift bookings</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Week Navigation</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <ArrowPathIcon className="h-5 w-5 text-red-600" />
                  <div>
                    <span className="font-medium text-gray-900">Previous Week</span>
                    <p className="text-sm text-gray-600">Navigate to earlier weeks</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="bg-gray-200 rounded px-3 py-1">
                    <span className="font-medium text-gray-900">Current Week Display</span>
                  </div>
                  <p className="text-sm text-gray-600">Shows selected week date range</p>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <ArrowPathIcon className="h-5 w-5 text-emerald-600" />
                  <div>
                    <span className="font-medium text-gray-900">Next Week</span>
                    <p className="text-sm text-gray-600">Navigate to future weeks</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Shift Entry Guide */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <ClockIcon className="h-6 w-6 text-emerald-600 mr-2" />
            Entering Shift Times
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Time Input Fields</h3>
              <div className="space-y-4">
                <div className="border rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <input type="time" className="border rounded px-2 py-1 text-sm" readOnly />
                    <span className="text-gray-600">to</span>
                    <input type="time" className="border rounded px-2 py-1 text-sm" readOnly />
                  </div>
                  <p className="text-sm text-gray-600">Enter start time first, then end time for each day</p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-yellow-800">Time Format</p>
                      <p className="text-sm text-yellow-700">Use 24-hour format (14:30 for 2:30 PM)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Status Indicators</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg">
                  <SunIcon className="h-5 w-5 text-red-600" />
                  <div>
                    <span className="font-medium text-red-800">Holiday</span>
                    <p className="text-sm text-red-700">Staff member is on holiday - cannot book shifts</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                  <CheckCircleIcon className="h-5 w-5 text-green-600" />
                  <div>
                    <span className="font-medium text-green-800">Booked Shift</span>
                    <p className="text-sm text-green-700">Shift already scheduled - shows times with delete option</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                  <ClockIcon className="h-5 w-5 text-blue-600" />
                  <div>
                    <span className="font-medium text-blue-800">Available</span>
                    <p className="text-sm text-blue-700">Ready for shift entry - time inputs are active</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Calculations and Compliance */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <CurrencyPoundIcon className="h-6 w-6 text-emerald-600 mr-2" />
            Automatic Calculations
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-emerald-50 rounded-lg">
              <ClockIcon className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900 mb-2">Working Hours</h3>
              <p className="text-sm text-gray-600">Automatically calculated with break deductions for shifts 6+ hours</p>
              <div className="mt-2 text-xs text-gray-500">
                Break policy applied per role
              </div>
            </div>

            <div className="text-center p-4 bg-teal-50 rounded-lg">
              <CalendarDaysIcon className="h-8 w-8 text-teal-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900 mb-2">Working Days</h3>
              <p className="text-sm text-gray-600">Counts days with scheduled shifts against contracted days</p>
              <div className="mt-2 text-xs text-gray-500">
                Compares to contract requirements
              </div>
            </div>

            <div className="text-center p-4 bg-cyan-50 rounded-lg">
              <CurrencyPoundIcon className="h-8 w-8 text-cyan-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900 mb-2">Daily Costs</h3>
              <p className="text-sm text-gray-600">Real-time cost calculation per day and hourly rate</p>
              <div className="mt-2 text-xs text-gray-500">
                Based on staff hourly rates
              </div>
            </div>
          </div>

          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900 mb-1">Contract Compliance</h4>
                <p className="text-sm text-blue-800">
                  System automatically compares scheduled hours/days against contracted amounts.
                  Green text indicates compliance, red text shows over/under scheduling.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Publishing Process */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <DocumentCheckIcon className="h-6 w-6 text-emerald-600 mr-2" />
            Publishing Shifts
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Publish Process</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="bg-emerald-100 rounded-full w-6 h-6 flex items-center justify-center mt-0.5">
                    <span className="text-sm font-bold text-emerald-600">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Review All Shifts</p>
                    <p className="text-sm text-gray-600">Check times, totals, and costs before publishing</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-emerald-100 rounded-full w-6 h-6 flex items-center justify-center mt-0.5">
                    <span className="text-sm font-bold text-emerald-600">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Click Publish</p>
                    <p className="text-sm text-gray-600">Use the green "Publish" button for each staff member</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-emerald-100 rounded-full w-6 h-6 flex items-center justify-center mt-0.5">
                    <span className="text-sm font-bold text-emerald-600">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Email Notification</p>
                    <p className="text-sm text-gray-600">Staff automatically receive email confirmations</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Success Confirmation</h3>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="h-6 w-6 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-900 mb-1">Shifts saved successfully!</p>
                    <p className="text-sm text-green-800">
                      Green success message appears at the top of the screen.
                      Shifts are now visible in the booked shifts view.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Standby Cover Management ─── */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <UserGroupIcon className="h-6 w-6 text-emerald-600 mr-2" />
            Standby Cover Management
          </h2>

          <div className="space-y-6">
            {/* Overview */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <ShieldCheckIcon className="h-6 w-6 text-indigo-600 mt-0.5" />
                <div>
                  <h3 className="font-medium text-indigo-900 mb-1">What is Standby Cover?</h3>
                  <p className="text-sm text-indigo-800">
                    Standby cover allows managers to pre‑select a list of volunteers who can step in when a staff member
                    is unavailable. When a shift needs covering, the system automatically contacts volunteers in priority
                    order until someone accepts.
                  </p>
                </div>
              </div>
            </div>

            {/* How It Works */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <ArrowPathIcon className="h-5 w-5 text-emerald-600 mr-2" />
                The Standby Process
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="bg-indigo-100 rounded-full w-6 h-6 flex items-center justify-center mt-0.5">
                      <span className="text-sm font-bold text-indigo-600">1</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Manager Sets Up Standby List</p>
                      <p className="text-sm text-gray-600">
                        When creating a shift, the manager selects multiple staff members as standby volunteers.
                        They are prioritised in the order they are added.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="bg-indigo-100 rounded-full w-6 h-6 flex items-center justify-center mt-0.5">
                      <span className="text-sm font-bold text-indigo-600">2</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Shift Requires Cover</p>
                      <p className="text-sm text-gray-600">
                        When the original staff member cannot work, the manager clicks "Request Cover".
                        The system then emails the first volunteer on the standby list.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="bg-indigo-100 rounded-full w-6 h-6 flex items-center justify-center mt-0.5">
                      <span className="text-sm font-bold text-indigo-600">3</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Volunteer Receives Email</p>
                      <p className="text-sm text-gray-600">
                        The email contains personalised Accept / Decline links. There is <strong>no time limit</strong> –
                        the request remains active until a response is given or all volunteers have been exhausted.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="bg-indigo-100 rounded-full w-6 h-6 flex items-center justify-center mt-0.5">
                      <span className="text-sm font-bold text-indigo-600">4</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Acceptance → Shift Confirmed</p>
                      <p className="text-sm text-gray-600">
                        If the volunteer accepts, the shift is automatically assigned to them. The original shift is marked as
                        covered, and a new shift entry is created for the volunteer. Both the manager and volunteer receive
                        confirmation emails.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="bg-indigo-100 rounded-full w-6 h-6 flex items-center justify-center mt-0.5">
                      <span className="text-sm font-bold text-indigo-600">5</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Decline → Next Volunteer Contacted</p>
                      <p className="text-sm text-gray-600">
                        If the volunteer declines, the system automatically contacts the next person on the list.
                        This continues until someone accepts or the list is empty. The manager is notified of each decline.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="bg-indigo-100 rounded-full w-6 h-6 flex items-center justify-center mt-0.5">
                      <span className="text-sm font-bold text-indigo-600">6</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">No Volunteers Left</p>
                      <p className="text-sm text-gray-600">
                        If all volunteers decline, the manager receives a notification and must handle the cover manually.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Volunteer Response Page */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <DocumentCheckIcon className="h-5 w-5 text-emerald-600 mr-2" />
                Volunteer Response Interface
              </h3>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <p className="text-sm text-gray-700 mb-3">
                  When a volunteer clicks the link in their email, they see a professional, branded page that matches the
                  InsightOS design language. The page displays:
                </p>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 ml-2">
                  <li>Clear action buttons: <strong>Accept</strong> or <strong>Decline</strong> the shift.</li>
                  <li>Confirmation step – they must confirm their choice before submission.</li>
                  <li>Success/error messages after submission.</li>
                  <li>No countdown timer – the request stays valid indefinitely.</li>
                </ul>
                <div className="mt-3 flex items-center space-x-4 text-xs">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800">
                    <CheckCircleIcon className="h-4 w-4 mr-1" /> Accept
                  </span>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-rose-100 text-rose-800">
                    <XCircleIcon className="h-4 w-4 mr-1" /> Decline
                  </span>
                </div>
              </div>
            </div>

            {/* Email Notifications */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <EnvelopeIcon className="h-5 w-5 text-emerald-600 mr-2" />
                Email Notifications
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-1">To the Volunteer</h4>
                  <p className="text-sm text-blue-800">
                    Each volunteer receives an email with the shift details (date, time) and personalised Accept/Decline links.
                    The email is clear and action‑oriented.
                  </p>
                </div>
                <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4">
                  <h4 className="font-medium text-emerald-900 mb-1">To the Manager</h4>
                  <p className="text-sm text-emerald-800">
                    Managers are notified when a volunteer accepts or declines. If a volunteer declines, they are informed
                    who has been contacted next. If all decline, they receive a final alert.
                  </p>
                </div>
              </div>
            </div>

            {/* Database Updates */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <CogIcon className="h-5 w-5 text-purple-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-purple-900 mb-1">Automatic Database Updates</h4>
                  <p className="text-sm text-purple-800">
                    When a volunteer accepts, the system:
                  </p>
                  <ul className="list-disc list-inside text-sm text-purple-800 space-y-0.5 ml-4">
                    <li>Updates the original shift to mark it as covered (sets <code>Standby</code> and <code>ReplacementID</code>).</li>
                    <li>Creates a new shift entry for the volunteer.</li>
                    <li>Updates the TaskCalendar to reflect the new staff assignment.</li>
                    <li>Clears the standby token so the link cannot be reused.</li>
                  </ul>
                  <p className="text-sm text-purple-800 mt-2">
                    On decline, the system logs the decline, removes that volunteer from the list, and generates a new token
                    for the next volunteer (if any).
                  </p>
                </div>
              </div>
            </div>

            {/* Best Practices */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-emerald-600 mr-2" />
                Standby Best Practices
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="h-5 w-5 text-emerald-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Keep Lists Updated</h4>
                    <p className="text-sm text-gray-600">Regularly review and refresh standby volunteer lists to ensure availability.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="h-5 w-5 text-emerald-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Communicate Expectations</h4>
                    <p className="text-sm text-gray-600">Inform staff that they may receive standby requests and how to respond.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="h-5 w-5 text-emerald-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Monitor Responses</h4>
                    <p className="text-sm text-gray-600">Managers should monitor email notifications to track cover progress.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="h-5 w-5 text-emerald-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">No Time Pressure</h4>
                    <p className="text-sm text-gray-600">With no expiry, volunteers can respond when convenient, reducing stress.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 mr-2" />
            Troubleshooting
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="border border-red-200 bg-red-50 rounded-lg p-4">
                <h4 className="font-medium text-red-900 mb-2">Cannot Book on Holiday</h4>
                <p className="text-sm text-red-800">
                  If a staff member shows as "Holiday", they cannot have shifts scheduled.
                  Check holiday bookings first or contact HR to adjust holiday dates.
                </p>
              </div>

              <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-4">
                <h4 className="font-medium text-yellow-900 mb-2">Break Deductions Not Applied</h4>
                <p className="text-sm text-yellow-800">
                  Breaks are only deducted for shifts of 6 hours or more.
                  Check role policies to ensure break lengths are configured correctly.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Email Notifications Not Sent</h4>
                <p className="text-sm text-blue-800">
                  If staff don't receive emails, check their email addresses in staff details.
                  Email notifications are sent automatically upon successful publishing.
                </p>
              </div>

              <div className="border border-purple-200 bg-purple-50 rounded-lg p-4">
                <h4 className="font-medium text-purple-900 mb-2">Cost Calculations Incorrect</h4>
                <p className="text-sm text-purple-800">
                  Verify staff hourly rates in staff management.
                  Costs are calculated in real-time based on scheduled hours and rates.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <CheckCircleIcon className="h-6 w-6 text-emerald-600 mr-2" />
            Best Practices
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <CheckCircleIcon className="h-5 w-5 text-emerald-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Plan Ahead</h4>
                  <p className="text-sm text-gray-600">Schedule shifts at least 1-2 weeks in advance to allow staff planning time.</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <CheckCircleIcon className="h-5 w-5 text-emerald-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Check Contracts</h4>
                  <p className="text-sm text-gray-600">Always verify scheduled hours/days don't exceed contracted amounts.</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <CheckCircleIcon className="h-5 w-5 text-emerald-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Review Costs</h4>
                  <p className="text-sm text-gray-600">Monitor daily costs to stay within budget allocations.</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <CheckCircleIcon className="h-5 w-5 text-emerald-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Use Copy Feature</h4>
                  <p className="text-sm text-gray-600">For recurring schedules, use "Copy For Next Week" to save time.</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <CheckCircleIcon className="h-5 w-5 text-emerald-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Confirm Publishing</h4>
                  <p className="text-sm text-gray-600">Always review all shifts before publishing to avoid errors.</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <CheckCircleIcon className="h-5 w-5 text-emerald-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Monitor Compliance</h4>
                  <p className="text-sm text-gray-600">Regularly check break policies and working time regulations.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookShiftsHelp;